import { ReactElement } from 'react';
import { TextInput } from '@components/common';
import { useFormik } from 'formik';
import { ToastType, useTosterStore } from '@store/toster';
import { keyDownEvent } from '@helper/utils';
import { useContractorDetailsMutation } from '@services/hooks/site-leads/site-leads-ss/useContractorMutation';
import * as Yup from 'yup';
import { IContractorDetailsResponse } from '@services/hooks/site-leads/site-leads-ss';

export interface IContractorDetailsProps {
	isContractorExists: boolean;
	name: string;
	phone: string;
	contractorDetails: IContractorDetailsResponse | null;
	onCancel: () => void;
	onSubmit: (name: string, appIdC?: string) => void; // Updated to include app_id_c
}

export interface IContractorCreatePayload {
	name: string;
	phone: string;
}

export const ContractorSSDetailsModal = ({
	name,
	phone,
	onCancel,
	onSubmit,
}: IContractorDetailsProps): ReactElement => {
	const { setToaster } = useTosterStore();
	const { isPending: isContractorDetailsPending, mutate: contractorDetailsMutation } = useContractorDetailsMutation();

	const validationSchema = Yup.object().shape({
		name: Yup.string().required('Contractor name is required'),
		phone: Yup.string()
			.matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
			.required('Phone number is required'),
	});

	const formik = useFormik({
		initialValues: {
			name: name || '',
			phone: phone || '',
		},
		validationSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			const payload: IContractorCreatePayload = {
				name: values.name,
				phone: values.phone,
			};

			contractorDetailsMutation(payload, {
				onSuccess: (response) => {
					onSubmit(values.name, response.app_id_c); // Pass app_id_c to parent
					onCancel();
				},
				onError: (error: Error) => {
					setToaster(true, ToastType.error, error.message || 'Failed to create contractor');
				},
			});
		},
	});

	return (
		<div className="ss-modal-wrapper">
			<form
				onSubmit={(e) => {
					formik.handleSubmit(e);
				}}
			>
				<div className="row">
					<div className="form-control d-col">
						<TextInput
							label="Name"
							id="name"
							name="name"
							value={formik.values.name}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							error={formik.touched.name && formik.errors.name ? formik.errors.name : null}
							required
						/>
					</div>
					<div className="form-control d-col">
						<TextInput
							label="Phone Number"
							id="phone"
							name="phone"
							value={formik.values.phone}
							setFieldValue={formik.setFieldValue}
							onBlur={formik.handleBlur}
							onKeyDown={keyDownEvent}
							error={formik.touched.phone && formik.errors.phone ? formik.errors.phone : null}
							disabled
							required
						/>
					</div>
				</div>
				<div className="btn-wrapper">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={() => {
							formik.resetForm();
							onCancel();
						}}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={!formik.isValid || !formik.dirty || isContractorDetailsPending}
					>
						{isContractorDetailsPending ? 'Submitting...' : 'Submit'}
					</button>
				</div>
			</form>
		</div>
	);
};
